import React, { FC, useEffect, useMemo } from 'react';
import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { ProductGroup } from 'ts/client/gallery/components';
import {
    convertIEventPhotosToIImages,
    getItemDisplayName,
    hasRenderableThumbnail,
    sortByDisplayOrder
} from 'ts/client/gallery/components/common';
import { ExpandableHtml } from 'ts/common/components';
import { GalleryHeading } from 'ts/common/components/gallery';
import { getAssetMediaType, getProductMediaTypes } from 'ts/common/mediaType';
import { useStore } from 'client_react/gallery/hooks/StoreContext';
import { PageIndex } from '../StorePage';

const GroupDescription = styled(Text)`
    p {
        color: inherit;
        font-size: inherit;
        line-height: inherit;
    }
`;

const SelectProductPage: FC = () => {
    const {
        goToPage,
        photos,
        priceSheetGroups,
        selectedPriceSheetGroup,
        setImageOnSelectedGroup,
        setSelectedPriceSheetGroup,
        setSelectedPriceSheetItem
    } = useStore();

    const gap = 16;
    const responsiveColumnGap = useBreakpointValue({ base: gap / 2, lg: gap }) ?? gap;
    const productGroupMaxWidth = useBreakpointValue({
        base: `calc(50% - ${responsiveColumnGap / 2}px)`,
        md: `calc(33% - ${gap / 2 + 1}px)`
    });

    useEffect(() => {
        setSelectedPriceSheetGroup(
            (selectedPriceSheetGroup) =>
                priceSheetGroups.find(
                    (priceSheetGroup) => priceSheetGroup.id === selectedPriceSheetGroup?.id
                ) ?? selectedPriceSheetGroup
        );
    }, [priceSheetGroups, setSelectedPriceSheetGroup]);

    const { description, groupType, items = [], name = '' } = selectedPriceSheetGroup || {};

    const isDigitalsGroupType = groupType === 'digitals';

    const itemList = [...Object.values(items)];
    const sortedItems = isDigitalsGroupType ? itemList : sortByDisplayOrder(itemList);
    const isOrnamentsGroupType = groupType === 'ornaments';
    const { boundedItems, customSubgroups, unboundedItems } = sortedItems.reduce(
        ({ boundedItems, customSubgroups, unboundedItems }, item) => {
            const subgroupName = item.subgroup?.name;

            if (
                ['packages', 'products'].includes(groupType ?? '') ||
                subgroupName === null ||
                subgroupName === 'No Size Specified'
            ) {
                unboundedItems.push(item);
            } else if (isOrnamentsGroupType && subgroupName) {
                if (!customSubgroups['ornaments']) {
                    customSubgroups['ornaments'] = [];
                }

                if (!customSubgroups['ornaments'].find((x) => x.subgroup?.name === subgroupName)) {
                    customSubgroups['ornaments'].push(item);
                }
            } else if (isDigitalsGroupType) {
                unboundedItems.push(item);
            } else if (item.bounds) {
                boundedItems.push(item);
            } else if (subgroupName) {
                if (!customSubgroups[subgroupName]) {
                    customSubgroups[subgroupName] = [];
                }

                customSubgroups[subgroupName].push(item);
            }

            return { boundedItems, customSubgroups, unboundedItems };
        },
        {
            boundedItems: [] as SpApi.Client.IPriceSheetItem[],
            customSubgroups: {} as { [subgroupName: string]: SpApi.Client.IPriceSheetItem[] },
            unboundedItems: [] as SpApi.Client.IPriceSheetItem[]
        }
    );

    // Only assets the conversion below will keep count as available, or a video still waiting on
    // its poster would let a mixed package through and then be dropped from its own sample.
    const availableMediaTypes = useMemo(
        () =>
            new Set(photos.filter(hasRenderableThumbnail).map((photo) => getAssetMediaType(photo))),
        [photos]
    );

    /**
     * One sample per tile, drawn from the assets that tile's products actually sell — a photo
     * standing in for a Video Download misrepresents it, and clicking the tile carries the sample
     * forward as the preselected asset, so a wrong draw follows the client into checkout.
     *
     * A product the gallery cannot complete gets no sample, and so no tile: every media type it
     * sells has to be on hand, or the client reaches the photo picker with nothing to choose for
     * one of the product's parts. A package answers for its children, so a mixed one needs all of
     * theirs — which is also why the media types come from getProductMediaTypes rather than the
     * display media type, since that collapses every mixed package to photo.
     */
    const sampleImages = useMemo(() => {
        const pickSampleImage = (tileItems: SpApi.Client.IPriceSheetItem[]) => {
            const sellableMediaTypes = tileItems.flatMap((item) => getProductMediaTypes(item));

            if (!sellableMediaTypes.every((mediaType) => availableMediaTypes.has(mediaType))) {
                return null;
            }

            const sellable = new Set(sellableMediaTypes);
            const [image] = convertIEventPhotosToIImages(
                photos.filter((photo) => sellable.has(getAssetMediaType(photo))),
                true,
                1
            );

            return image ?? null;
        };

        return {
            bounded: boundedItems.length > 0 ? pickSampleImage(boundedItems) : null,
            subgroups: Object.values(customSubgroups).map((subgroupItems) =>
                pickSampleImage(subgroupItems)
            ),
            unbounded: unboundedItems.map((item) => pickSampleImage([item]))
        };
        // Keyed on items and groupType, which the grouped arrays are rebuilt from every render -
        // keying on the arrays themselves would reshuffle every sample on each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableMediaTypes, groupType, items, photos]);

    // Tiles with no sample are ones the gallery cannot complete, and are left out.
    const boundedImage = sampleImages.bounded;
    const unboundedTiles = unboundedItems.flatMap((item, index) => {
        const image = sampleImages.unbounded[index];

        return image ? [{ image, item }] : [];
    });
    const subgroupTiles = Object.entries(customSubgroups).flatMap(
        ([subgroupName, items], index) => {
            const image = sampleImages.subgroups[index];

            return image ? [{ image, items, subgroupName }] : [];
        }
    );

    if (!selectedPriceSheetGroup || !photos) {
        return null;
    }

    const boundedItemsPriceSheetGroup = { ...selectedPriceSheetGroup, items: boundedItems };

    const goToSelectProductOptionsSlideWithItem = (
        priceSheetItem: SpApi.Client.IPriceSheetItem
    ) => {
        setSelectedPriceSheetItem(priceSheetItem);
        goToPage(PageIndex.SelectProductOptions, priceSheetItem.name);
    };

    const goToSelectProductOptionsSlideWithGroup = (
        priceSheetGroup: SpApi.Client.IPriceSheetGroup
    ) => {
        const { items } = priceSheetGroup;

        if (items.length > 1) {
            setSelectedPriceSheetItem(null);
            setSelectedPriceSheetGroup(priceSheetGroup);
        } else {
            setSelectedPriceSheetItem(items[0]);
        }

        // TODO: correct?
        goToPage(PageIndex.SelectProductOptions, priceSheetGroup.name);
    };

    return (
        <Box maxWidth="1440px" padding="48px 20px">
            <GalleryHeading
                marginBottom="16px"
                size="md"
                text={name}
                variant="primary"
                data-testid="select-product-heading"
            />
            {description && (
                <GroupDescription
                    as="span"
                    fontSize="md"
                    marginBottom="24px"
                    data-testid="select-product-description"
                >
                    <ExpandableHtml html={description} maxLineCount={4} />
                </GroupDescription>
            )}
            {(boundedImage || unboundedTiles.length > 0) && (
                <Flex
                    flexWrap="wrap"
                    columnGap={`${responsiveColumnGap}px`}
                    rowGap="20px"
                    marginBottom="24px"
                >
                    {boundedImage && (
                        <ProductGroup
                            image={boundedImage}
                            maxWidth={productGroupMaxWidth}
                            onClick={() => {
                                setImageOnSelectedGroup(boundedImage);
                                goToSelectProductOptionsSlideWithGroup(boundedItemsPriceSheetGroup);
                            }}
                            priceSheetGroup={boundedItemsPriceSheetGroup}
                        />
                    )}
                    {unboundedTiles.map(({ image, item }, index) => (
                        <ProductGroup
                            image={image}
                            key={index}
                            maxWidth={productGroupMaxWidth}
                            onClick={() => {
                                setImageOnSelectedGroup(image);
                                goToSelectProductOptionsSlideWithItem(item);
                            }}
                            priceSheetGroup={{
                                ...selectedPriceSheetGroup,
                                items: [item],
                                name: getItemDisplayName(item)
                            }}
                        />
                    ))}
                </Flex>
            )}
            {subgroupTiles.length > 0 &&
                subgroupTiles.map(({ image, items, subgroupName }, subgroupIndex) => (
                    <React.Fragment key={subgroupIndex}>
                        {!isOrnamentsGroupType && (
                            <GalleryHeading
                                marginBottom="28px"
                                size="sm"
                                text={subgroupName}
                                variant="primary"
                            />
                        )}
                        <Flex
                            flexWrap="wrap"
                            columnGap={`${responsiveColumnGap}px`}
                            rowGap="20px"
                            marginBottom="24px"
                        >
                            {items.map((item, itemIndex) => (
                                <ProductGroup
                                    image={image}
                                    key={itemIndex}
                                    maxWidth={productGroupMaxWidth}
                                    onClick={() => {
                                        setImageOnSelectedGroup(image);
                                        goToSelectProductOptionsSlideWithItem(item);
                                    }}
                                    priceSheetGroup={{
                                        ...selectedPriceSheetGroup,
                                        items: [item],
                                        name:
                                            isOrnamentsGroupType &&
                                            item.subgroup &&
                                            item.subgroup.name
                                                ? item.subgroup.name
                                                : item.name
                                    }}
                                />
                            ))}
                        </Flex>
                    </React.Fragment>
                ))}
        </Box>
    );
};

export default SelectProductPage;
